import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { backtestApi } from '@/services/api/backtest'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { BarChart3, TrendingUp, TrendingDown, Loader2, Download } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import type { Trade } from '@/services/api/backtest'
import { showErrorToast, showSuccessToast } from '@/utils/error-handler'

interface BacktestResultsViewProps {
  jobId: string
}

export default function BacktestResultsView({ jobId }: BacktestResultsViewProps) {
  const [isDownloading, setIsDownloading] = useState(false)
  const [tradesPage, setTradesPage] = useState(1)
  const [tradesPageSize, setTradesPageSize] = useState(50)
  
  const { data: backtestResult, isLoading } = useQuery({
    queryKey: ['backtest', jobId],
    queryFn: () => backtestApi.get(jobId),
    enabled: !!jobId,
    retry: false,
  })

  // Fetch paginated trades
  const { data: tradesData, isLoading: isLoadingTrades } = useQuery({
    queryKey: ['backtest-trades', jobId, tradesPage, tradesPageSize],
    queryFn: () => backtestApi.getTrades(jobId, { 
      limit: tradesPageSize, 
      offset: (tradesPage - 1) * tradesPageSize 
    }),
    enabled: !!jobId && !!backtestResult,
    retry: false,
  })

  // Use paginated trades if available, otherwise fallback to backtestResult.trades
  const displayTrades = tradesData?.trades || backtestResult?.trades?.slice(0, tradesPageSize) || []
  const tradesTotal = tradesData?.pagination?.total || backtestResult?.trades?.length || 0
  const tradesTotalPages = tradesData?.pagination 
    ? Math.ceil(tradesTotal / tradesPageSize)
    : (backtestResult?.trades ? Math.ceil(backtestResult.trades.length / tradesPageSize) : 1)

  const handleDownloadCSV = async (jobId: string) => {
    setIsDownloading(true)
    try {
      // Fetch all trades using pagination
      let allTrades: Trade[] = []
      let offset = 0
      const limit = 1000
      let hasMore = true

      while (hasMore) {
        const result = await backtestApi.getTrades(jobId, { limit, offset })
        allTrades = [...allTrades, ...result.trades]
        hasMore = result.pagination.hasMore
        offset += limit
      }

      if (allTrades.length === 0) {
        showErrorToast('No trades available to download')
        return
      }

      // Convert trades to CSV
      const csvHeaders = [
        'ID',
        'Symbol',
        'Signal',
        'Direction',
        'Size',
        'Entry Timestamp',
        'Exit Timestamp',
        'Entry Price',
        'Exit Price',
        'P&L',
        'P&L %',
        'Exit Reason',
        'Duration (hours)',
      ]

      const csvRows = allTrades.map((trade) => [
        trade.id || '',
        trade.symbol || '',
        trade.signalName || '',
        (trade.direction || '').toUpperCase(),
        (trade.size || 0).toString(),
        trade.entryTimestamp ? new Date(trade.entryTimestamp * 1000).toISOString() : '',
        trade.exitTimestamp ? new Date(trade.exitTimestamp * 1000).toISOString() : '',
        (trade.entryPrice || 0).toFixed(2),
        (trade.exitPrice || 0).toFixed(2),
        (trade.pnl || 0).toFixed(2),
        (trade.pnlPercent || 0).toFixed(2),
        trade.exitReason || '',
        trade.duration ? (trade.duration / 3600).toFixed(2) : '0.00',
      ])

      // Escape CSV values (handle commas, quotes, newlines)
      const escapeCSV = (value: string): string => {
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""')}"`
        }
        return value
      }

      // Build CSV content
      const csvContent = [
        csvHeaders.map(escapeCSV).join(','),
        ...csvRows.map((row) => row.map((cell) => escapeCSV(String(cell))).join(',')),
      ].join('\n')

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `backtest-trades-${jobId.slice(0, 8)}-${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      showSuccessToast(`Downloaded ${allTrades.length} trades as CSV`)
    } catch (error) {
      console.error('Failed to download CSV:', error)
      showErrorToast(error instanceof Error ? error : new Error('Failed to download trades CSV'))
    } finally {
      setIsDownloading(false)
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading backtest results...
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!backtestResult) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Backtest results not available</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Performance Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Performance Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Initial Capital</p>
              <p className="text-2xl font-bold">${backtestResult.metrics.initialCapital.toLocaleString()}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Final Capital</p>
              <p className={`text-2xl font-bold ${backtestResult.metrics.finalCapital >= backtestResult.metrics.initialCapital ? 'text-green-600' : 'text-red-600'}`}>
                ${backtestResult.metrics.finalCapital.toLocaleString()}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total Return</p>
              <p className={`text-2xl font-bold flex items-center gap-1 ${backtestResult.metrics.totalReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {backtestResult.metrics.totalReturn >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                {backtestResult.metrics.totalReturn.toFixed(2)}%
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total Trades</p>
              <p className="text-2xl font-bold">{backtestResult.metrics.totalTrades}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Trading Statistics */}
        <Card>
          <CardHeader>
            <CardTitle>Trading Statistics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Winning Trades</span>
              <span className="font-medium text-green-600">{backtestResult.metrics.winningTrades}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Losing Trades</span>
              <span className="font-medium text-red-600">{backtestResult.metrics.losingTrades}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Win Rate</span>
              <span className="font-medium">{(typeof backtestResult.metrics.winRate === 'number' ? Math.min(100, backtestResult.metrics.winRate <= 1 ? backtestResult.metrics.winRate * 100 : backtestResult.metrics.winRate) : 0).toFixed(2)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Profit Factor</span>
              <span className="font-medium">{backtestResult.metrics.profitFactor.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Risk Metrics */}
        <Card>
          <CardHeader>
            <CardTitle>Risk Metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Max Drawdown</span>
              <span className="font-medium text-red-600">{backtestResult.metrics.maxDrawdown.toFixed(2)}%</span>
            </div>
            {backtestResult.metrics.sharpeRatio !== null && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Sharpe Ratio</span>
                <span className="font-medium">{backtestResult.metrics.sharpeRatio.toFixed(2)}</span>
              </div>
            )}
            {backtestResult.metrics.sortinoRatio !== null && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Sortino Ratio</span>
                <span className="font-medium">{backtestResult.metrics.sortinoRatio.toFixed(2)}</span>
              </div>
            )}
            {backtestResult.metrics.calmarRatio !== null && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Calmar Ratio</span>
                <span className="font-medium">{backtestResult.metrics.calmarRatio.toFixed(2)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Equity Curve */}
      {backtestResult.equityCurve && backtestResult.equityCurve.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Equity Curve</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={backtestResult.equityCurve.map(point => ({
                  timestamp: new Date(point.timestamp * 1000).toLocaleDateString(),
                  equity: point.equity
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    domain={['dataMin', 'dataMax']}
                  />
                  <Tooltip
                    formatter={(value: any) => [`$${Number(value).toFixed(2)}`, 'Equity']}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="equity"
                    stroke="#0088FE"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trades Table */}
      {(backtestResult.trades && backtestResult.trades.length > 0) || (tradesData && tradesData.trades.length > 0) ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Trades ({tradesTotal})</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDownloadCSV(jobId)}
                disabled={isDownloading}
                className="flex items-center gap-2"
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Download CSV
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoadingTrades ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="overflow-auto max-h-96 border rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Symbol</th>
                        <th className="px-4 py-2 text-left font-medium">Signal</th>
                        <th className="px-4 py-2 text-left font-medium">Direction</th>
                        <th className="px-4 py-2 text-left font-medium">Size</th>
                        <th className="px-4 py-2 text-left font-medium">Entry</th>
                        <th className="px-4 py-2 text-left font-medium">Exit</th>
                        <th className="px-4 py-2 text-left font-medium">Entry Price</th>
                        <th className="px-4 py-2 text-left font-medium">Exit Price</th>
                        <th className="px-4 py-2 text-left font-medium">P&L</th>
                        <th className="px-4 py-2 text-left font-medium">P&L %</th>
                        <th className="px-4 py-2 text-left font-medium">Exit Reason</th>
                        <th className="px-4 py-2 text-left font-medium">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayTrades.map((trade) => (
                        <tr key={trade.id} className="border-t hover:bg-muted/50">
                          <td className="px-4 py-2 font-medium">
                            {trade.symbol || '-'}
                          </td>
                          <td className="px-4 py-2">
                            <span className="text-xs font-medium text-muted-foreground">
                              {trade.signalName || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            <Badge variant={(trade.direction || '').toLowerCase() === 'long' ? 'default' : 'secondary'}>
                              {(trade.direction || 'N/A').toUpperCase()}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 font-medium">
                            {trade.size || 1}
                          </td>
                          <td className="px-4 py-2 text-xs">
                            {new Date(trade.entryTimestamp * 1000).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-xs">
                            {new Date(trade.exitTimestamp * 1000).toLocaleString()}
                          </td>
                          <td className="px-4 py-2">${trade.entryPrice ? trade.entryPrice.toFixed(2) : 'N/A'}</td>
                          <td className="px-4 py-2">${trade.exitPrice ? trade.exitPrice.toFixed(2) : 'N/A'}</td>
                          <td className={`px-4 py-2 font-medium ${trade.pnl && trade.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {trade.pnl ? (trade.pnl >= 0 ? '+' : '') + trade.pnl.toFixed(2) : 'N/A'}
                          </td>
                          <td className={`px-4 py-2 font-medium ${trade.pnlPercent && trade.pnlPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {trade.pnlPercent ? (trade.pnlPercent >= 0 ? '+' : '') + trade.pnlPercent.toFixed(2) + '%' : 'N/A'}
                          </td>
                          <td className="px-4 py-2">
                            <Badge variant="outline" className="text-xs">
                              {trade.exitReason}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-xs">
                            {(trade.duration / 3600).toFixed(1)}h
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {tradesTotalPages > 1 && (
                  <Pagination
                    currentPage={tradesPage}
                    totalPages={tradesTotalPages}
                    pageSize={tradesPageSize}
                    totalItems={tradesTotal}
                    onPageChange={(page) => {
                      setTradesPage(page)
                      // Scroll to top of table when page changes
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }}
                    onPageSizeChange={(pageSize) => {
                      setTradesPageSize(pageSize)
                      setTradesPage(1)
                    }}
                    showPageSizeSelector={true}
                    pageSizeOptions={[25, 50, 100, 250, 500]}
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Diagnostics */}
      {backtestResult.diagnostics && (
        <Card>
          <CardHeader>
            <CardTitle>Diagnostics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {backtestResult.diagnostics.signalsGenerated !== undefined && (
                <div>
                  <p className="text-muted-foreground">Signals Generated</p>
                  <p className="font-medium">{backtestResult.diagnostics.signalsGenerated}</p>
                </div>
              )}
              {backtestResult.diagnostics.candlesProcessed !== undefined && (
                <div>
                  <p className="text-muted-foreground">Candles Processed</p>
                  <p className="font-medium">{backtestResult.diagnostics.candlesProcessed.toLocaleString()}</p>
                </div>
              )}
              {backtestResult.diagnostics.candlesWithSignals !== undefined && (
                <div>
                  <p className="text-muted-foreground">Candles with Signals</p>
                  <p className="font-medium">{backtestResult.diagnostics.candlesWithSignals}</p>
                </div>
              )}
              {backtestResult.diagnostics.candlesSkipped !== undefined && (
                <div>
                  <p className="text-muted-foreground">Candles Skipped</p>
                  <p className="font-medium">{backtestResult.diagnostics.candlesSkipped.toLocaleString()}</p>
                </div>
              )}
              {backtestResult.diagnostics.configuredSignals && (
                <div>
                  <p className="text-muted-foreground">Configured Signals</p>
                  <p className="font-medium">
                    Long: {backtestResult.diagnostics.configuredSignals.long?.length || 0}, 
                    Short: {backtestResult.diagnostics.configuredSignals.short?.length || 0}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
